import { computed, inject, Injectable, signal, type TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from '@reconcept-platform/shared/util';

export interface NavConfig {
  id?: string;
  rootPath?: string[];
  sideNavTemplate?: TemplateRef<any>;
  topLeftNavTemplate?: TemplateRef<any>;
  topRightNavTemplate?: TemplateRef<any>;
  topSingleTemplate?: TemplateRef<any>; // used for mobile, room for only 1 template
  showLogoutInSideNav?: boolean;
}

const LOCAL_STORAGE_KEY = 'sidebar-is-collapsed';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private router = inject(Router);
  private localStorage = inject(LocalStorageService);
  private navConfigs = signal<NavConfig[]>([]);

  sidebarIsCollapsed = signal<boolean>(false);

  constructor() {
    this.sidebarIsCollapsed.set(this.getLocalStoragePreference() || false);
  }

  setNavConfig(navIndex: number, navConfig: NavConfig) {
    this.navConfigs.update((navConfigs: NavConfig[]) => {
      navConfigs[navIndex] = navConfig;
      return [...navConfigs];
    });
  }

  unsetNavConfig(navConfig: NavConfig) {
    if (!navConfig) {
      return;
    }

    const index = this.navConfigs().indexOf(navConfig);

    if (index > -1) {
      this.navConfigs.update((navConfigs: NavConfig[]) => {
        navConfigs.splice(index, 1);
        return [...navConfigs];
      });
    }
  }

  goBack() {
    if (this.hasParentNavConfig()) {
      const currentNavConfig = this.navConfigs().pop();
      // @ts-expect-error TS2345 argument not assignable
      this.unsetNavConfig(currentNavConfig);
      const newConfig = this.getNavConfig();
      this.router.navigate(newConfig?.rootPath ? newConfig.rootPath : ['']);
    }
  }

  hasParentNavConfig = computed(() => {
    return this.navConfigs().length > 1;
  });

  parentNavIsRoot = computed(() => {
    return this.navConfigs().length === 2;
  });

  getNavConfig(): NavConfig {
    if (!this.navConfigs() || !this.navConfigs().length) {
      // @ts-expect-error TS2322 type not assignable
      return null;
    }
    return this.navConfigs()[this.navConfigs().length - 1];
  }

  expandSidebar() {
    this.sidebarIsCollapsed.set(false);
    this.setLocalStoragePreference(this.sidebarIsCollapsed());
  }

  collapseSidebar() {
    this.sidebarIsCollapsed.set(true);
    this.setLocalStoragePreference(this.sidebarIsCollapsed());
  }

  toggleSidebar($event?: any) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    this.sidebarIsCollapsed.set(!this.sidebarIsCollapsed());
    this.setLocalStoragePreference(this.sidebarIsCollapsed());
  }

  private getLocalStoragePreference(): boolean {
    return this.localStorage.retrieve(LOCAL_STORAGE_KEY);
  }

  private setLocalStoragePreference(isCollapsed: boolean): boolean {
    return this.localStorage.store(LOCAL_STORAGE_KEY, isCollapsed);
  }
}
